import React from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, Typography, alpha,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { ERROR, NAVY } from "../../theme/tokens";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  /** Red confirm button for destructive actions. */
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Standard confirm/destruction dialog. Replaces ad-hoc delete popups.
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  danger,
  loading,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const accent = danger ? ERROR : NAVY;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogContent sx={{ pt: 4, textAlign: "center" }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
            bgcolor: alpha(accent, 0.12),
          }}
        >
          <WarningAmberRoundedIcon sx={{ fontSize: 30, color: accent }} />
        </Box>
        <Typography fontSize={18} fontWeight={800} color="text.primary">
          {title}
        </Typography>
        {message && (
          <Typography fontSize={14} color="text.secondary" sx={{ mt: 1 }}>
            {message}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button fullWidth variant="outlined" onClick={onClose} sx={{ borderRadius: "10px" }}>
          {cancelText}
        </Button>
        <Button
          fullWidth
          variant="contained"
          color={danger ? "error" : "primary"}
          onClick={onConfirm}
          disabled={loading}
          sx={{ borderRadius: "10px" }}
        >
          {loading ? "Working…" : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
